/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {toast} from 'utils/toastUtils'
import {useParams} from 'react-router-dom'
import RecoveryAccessTokenRequested from './RecoveryAccessTokenRequested'
import ForgottenPasswordRecoveryForm from './ForgottenPasswordRecoveryForm'
import RecoveryAccessForm from './RecoveryAccessForm'
import RecoverySuccess from './RecoverySuccess'
import SellerAction from 'store/seller/SellerAction'

const forgottenPasswordSchema = z.object({
    email: z.string().trim().email('Enter a valid email address'),
})

type ForgottenPasswordInputs = z.infer<typeof forgottenPasswordSchema>

const ForgottenPasswordForm = () => {
    const dispatch = useAppDispatch()
    const { register, handleSubmit, formState: {errors} } = useForm<ForgottenPasswordInputs>({
        resolver: zodResolver(forgottenPasswordSchema),
    })
    const { t } = useTranslation()
    const { access_token, app_id, m_clk_id } = useParams()

    const [accessTokenRequested, setAccessTokenRequested] =
        useState<boolean>(false)
    const [accessGranted, setAccessGranted] = useState<boolean>(false)
    const [accessEmailRequested, setAccessEmailRequested] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const onSubmitPasswordForgotten = async (data: ForgottenPasswordInputs) => {
        if (isSubmitting) return
        setIsSubmitting(true)
        const app_url = window.location.hostname + ':' + window.location.port
        try {
            await dispatch(SellerAction.forgottenPassword(data.email, app_url))
            setAccessEmailRequested(data.email)
            setAccessTokenRequested(true)
            toast.success(t('Please check your email for access token'))
        } catch {
            toast.error(t('Unable to request password recovery. Please try again.'))
        } finally {
            setIsSubmitting(false)
        }
    }

    if (accessGranted) {
        return <RecoverySuccess />
    }

    if (access_token) {
        return (
            <RecoveryAccessForm
                accessEmailRequested={accessEmailRequested}
                setAccessGranted={setAccessGranted}
                params={{ access_token, app_id, m_clk_id }}
            />
        )
    }

    if (accessTokenRequested) {
        return <RecoveryAccessTokenRequested />
    }

    return (
        <ForgottenPasswordRecoveryForm
            register={register}
            onSubmit={handleSubmit(onSubmitPasswordForgotten)}
            isSubmitting={isSubmitting}
            emailError={errors.email?.message}
        />
    )
}

export default ForgottenPasswordForm
